/**
 * @module app.shared
 */

import { TaskQueue, autoinject } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { version } from 'version';
import { httpRequest } from 'app/shared/services/http-service';
import * as msal from "@azure/msal-browser";
import { SignOnEvent, SignOutEvent } from 'app/core/lib/signon-events';

/**
 * Custom MSAL Configuration
 */
export interface IEssetteMsalConfig extends IEssetteMsalOptions {
    /**
     * Allows specifying overrides of the default configuration's `auth` object.
     * For details on each configuration property, see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md#auth-config-options
     */
    readonly auth: msal.BrowserAuthOptions;
}

/**
 * Custom MSAL configuration options that are not part of the main MSAL Config object
 */
interface IEssetteMsalOptions {
    readonly login?: {
        /**
         * Force a login prompt and prevent MSAL from performing SSO from a cached active account
         */
        readonly forcePrompt?: boolean;
        /**
         * Login Request scopes.  By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
         */
        readonly requestScopes?: Array<string>;
        /**
         * Provides a hint about the domain that the user should use to sign in. The value of the domain hint is a registered domain for the tenant.
         * This can be useful in a B2C scenario where we want to maintain different sign on provider configurations, but bypass the IdP selection page.
         */
        readonly domainHint?: string;
        /**
         * String map of custom query parameters added to the /authorize call during login
         */
        readonly extraQueryParameters?: {
            [key: string]: string;
        };
    }
    readonly logout?: {
        /**
         * URI to navigate the app window to after logout is complete
         */
        redirectUri?: string;
    }
    readonly userInfo?: {
        /**
         * Enable an additional request for User Info after OIDC sign on
         */
        readonly enableRequest?: boolean;
        /**
         * Access token request scopes to fetch User's Profile data
         */
        readonly requestScopes?: Array<string>;
    }
    /**
     * Configurable options based on the origin IdP.  Mainly used in B2C scenarios.
     */
    readonly idp?: {
        /**
         * Unique identifier for the IdP, coming from the ID Token's `idp` claim value
         */
        [key: string]: {
            /**
             * URI to navigate the app window to after logout is complete
             */
            logoutRedirectUri?: string;
        }
    }
}

/**
 * MS Graph API response data about the user.
 */
export type UserInfo = {
    businessPhones?: Array<string>,
    displayName?: string,
    givenName?: string,
    id?: string,
    jobTitle?: string,
    mail?: string,
    mobilePhone?: string,
    officeLocation?: string,
    preferredLanguage?: string,
    surname?: string,
    userPrincipalName?: string
};

/**
 * MS Graph API error response
 */
type MSGraphError = {
    error: {
        code?: string,
        message?: string,
        innerError?: {
            date: Date,
            "request-id": string,
            "client-request-id": string
            code?: string;
            message?: string;
            target?: string;
        }
    }
}

/**
 * MS Graph API endpoint for the Signed In User Profile
 */
export const GRAPH_ME_ENDPOINT = "https://graph.microsoft.com/v1.0/me"

interface IEndSession {
    isSuccessful: boolean,
    readonly successPayload?: msal.EndSessionPopupRequest
    readonly failurePayload?: msal.AuthError
}


@autoinject
export class MsalService {

    private configOptions: IEssetteMsalOptions;
    private readonly defaultMsalConfig: msal.Configuration;
    private msalConfig?: msal.Configuration;
    private msalInstance: msal.PublicClientApplication | null = null;

    /** The Current Signed In Account Information */
    private account: msal.AccountInfo | null = null;
    /** Additional User Info associated to the Current Account */
    private userInfo: UserInfo | null = null;
    /** The ID Token of the Current Account */
    private idToken: string | null = null;

    // TODO[MW]: Find a better solution than tracking the logout process's success/failure in this transient object.
    // We really only want to attach a handler for a LOGOUT_END event if we first handle a success\failure event, then remove the handler.
    /** Information about the Session End process, whether successful or failed */
    private endSessionObject: IEndSession | null = null;

    // Default Options
    private loginRequestScopes: Array<string> = ["openid", "profile"];
    private userInfoRequestScopes: Array<string> = ["openid", "profile", "User.Read"];

    public get isInitialized(): boolean {
        return !!this.msalInstance;
    }

    /** Whether a Sign In process succeeded and a user is currently Signed In */
    public isSignedIn: boolean = false;  //TODO[MW]: Make this an enumerated state as part of a bigger "Sign On" state, shared by other Sign On Provider methods.
    // public get isSignedIn(): boolean {
    //     return !!(this.account && this.idToken);
    // }

    constructor(
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger,
        private readonly events: EventAggregator,
        private readonly taskQueue: TaskQueue
    ) {
        this.logger.source = this;

        // Set the application instance's default config
        this.defaultMsalConfig = this.getDefaultMsalConfig();
        this.configOptions = this.getDefaultMsalOptions();
    }

    /**
     * Initialize the MSAL library
     * @param configKey Key associated with custom configuration settings
     */
    public async init(configKey: string): Promise<void> {
        this.clear();
        await this.loadConfig(configKey);

        this.msalInstance = new msal.PublicClientApplication(this.msalConfig!);
        await this.msalInstance.initialize();
        this.msalInstance.handleRedirectPromise()
            .then(this.handleRequestResponse)
            .catch((error) => {
                // handle error, either in the library or coming back from the server
                this.logger.error("Error handling redirect", error);
                throw error;
            });

        // Handle emitted events
        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/events.md
        this.msalInstance.addEventCallback((message: msal.EventMessage) => {
            switch(message.eventType) {
                case msal.EventType.LOGOUT_SUCCESS:
                    this.handleMsalLogoutSuccessEvent(message);
                    break;
                case msal.EventType.LOGOUT_FAILURE:
                    if(message.error instanceof msal.AuthError) {
                        this.handleMsalLogoutFailureEvent(message);
                    }
                    break;
                case msal.EventType.LOGOUT_END:
                    this.handleMsalLogoutEndEvent(message);
                    break;
                default:
                    break;
            }
        });
    }

    /**
     * Reset properties so the MSAL library can be intialized with a new configuration
     */
    private clear() {
        this.msalInstance = null;
        this.msalConfig = undefined;
        this.account = null;
        this.userInfo = null;
        this.idToken = null;
    }

    /**
     * Get's the default MSAL configuration
     */
    private getDefaultMsalConfig(): msal.Configuration {
        return {
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
            // For Azure AD the `authority` takes the form of: https://login.microsoftonline.com/<tenant_id>
            // For Azure B2C the `authority` takes the form of: https://<your-tenant>.b2clogin.com/<your-tenant>.onmicrosoft.com/<your-policyID>
            // For Azure B2C the `knownAuthorities` takes the form of: ["<your-tenant>.b2clogin.com"]
            // For Azure B2C the tokenRequest scopes takes the form of: ["https://<your-tenant>.onmicrosoft.com/<your-api>/<your-scope>"]
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/working-with-b2c.md
            auth: {
                clientId: "",  //This must be overriden in the configuration
                authority: "https://login.microsoftonline.com/common/",
                knownAuthorities: [],  // Used for B2C Scenarios
                redirectUri: this.env.resolveUrl('~/signon/blank'),
                postLogoutRedirectUri: this.env.resolveUrl('~/signon/blank'),
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: "localStorage",
                storeAuthStateInCookie: false
            },
            system: {
                loggerOptions: {
                    logLevel: msal.LogLevel.Verbose,
                    loggerCallback: (level: msal.LogLevel, message: string, containsPii: boolean): void => {
                        if(containsPii && !this.env.config.debug) {
                            return;
                        }
                        switch(level) {
                            case msal.LogLevel.Error:
                                this.logger.error(message);
                                return;
                            case msal.LogLevel.Info:
                                this.logger.info(message);
                                return;
                            case msal.LogLevel.Verbose:
                                this.logger.debug(message);
                                return;
                            case msal.LogLevel.Warning:
                                this.logger.warn(message);
                                return;
                        }
                    }
                }
            },
            telemetry: {
                application: {
                    appName: "Essette",
                    appVersion: version.number
                }
            }
        };
    }

    /**
     * Get's the default MSAL configuration
     */
    private getDefaultMsalOptions(): IEssetteMsalOptions {
        return {
            login: {
                requestScopes: ["openid", "profile"]
            },
            userInfo: {
                enableRequest: false,
                requestScopes: ["openid", "profile", "User.Read"]
            }
        };
    }

    /**
     * Load a custom configuration by key from a custom configuration file, and merge it with the default configuration.
     * @param key Key associated with custom configuration settings
     */
    private async loadConfig(key: string): Promise<void> {
        try {
            // Load custom MSAL config properties, by configuration key
            const configUrl = this.env.resolveUrl('~/msal-config.json');
            const fileResponse: { [key: string]: IEssetteMsalConfig } = await httpRequest(configUrl, { format: 'json', cache: 'none' });
            if(!(fileResponse && typeof fileResponse === 'object')) {
                // This must exist, if only just to set the Client ID value
                throw new Error("msal-config.json Not Found")
            }

            let loadedConfig: IEssetteMsalConfig = fileResponse[key];
            if(loadedConfig && typeof loadedConfig === 'object') {
                this.logger.info("MSAL Configuration Loaded.");

                // merge default config with loaded config
                this.msalConfig = { ...this.defaultMsalConfig };
                Object.assign(this.msalConfig.auth, loadedConfig.auth);

                // Set custom scopes
                if(loadedConfig.login?.requestScopes) {
                    this.loginRequestScopes = loadedConfig.login.requestScopes;
                }
                if(loadedConfig.userInfo?.requestScopes) {
                    this.userInfoRequestScopes = loadedConfig.userInfo.requestScopes;
                }

                // merge default options with custom options
                const defaultOptions = this.getDefaultMsalOptions();
                const loadedOptions = loadedConfig as IEssetteMsalOptions;
                this.configOptions = { ...defaultOptions };
                Object.assign(this.configOptions, loadedOptions);
            }
        }
        catch(error) {
            this.logger.warn("Unable to load MSAL Configuration", error);
        }
    }

    // WARN[MW]: This does not currently work, since the redirect `response_mode` is fixed at "fragment" and does not currently work with our app.
    public async loginRedirect() {
        if(!this.msalInstance) {
            this.logger.error("MSAL is not Initalized");
            return;
        }

        const request: msal.RedirectRequest = {
            scopes: this.loginRequestScopes
        };

        try {
            this.msalInstance.loginRedirect(request);
        } catch(error) {
            this.logger.error("Login Redirect Error", error);
        }
    }

    /**
     * Login Process involving a popup
     * @param domainHint `optional` Provides a hint about the tenant or domain that the user should use to sign in.
     */
    public async loginPopup(domainHint?: string): Promise<void> {
        if(!this.msalInstance) {
            throw new Error("MSAL is not Initalized");
        }

        // Create the Request settings by merging configuration properties with passed in settings
        // Scopes you add here will be prompted for user consent during sign-in.  By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
        // When using popup APIs it's recommended to set the redirectUri to a blank page or a page that does not implement MSAL.
        const request: msal.PopupRequest = {
            scopes: this.loginRequestScopes,
            redirectUri: this.env.resolveUrl('~/signon/blank'),
            prompt: this.configOptions.login?.forcePrompt ? 'login' : undefined,
            domainHint: domainHint || this.configOptions.login?.domainHint,
            extraQueryParameters: this.configOptions.login?.extraQueryParameters
        };

        try {
            const response = await this.msalInstance.loginPopup(request);
            this.handleRequestResponse(response);
        }
        catch(error) {
            this.isSignedIn = false;
            this.logger.error(error);
            throw new Error("Login Error");
        }

        // Get User's Profile data, if available.  This isn't necessary, but may contain useful information for our user record.
        if(this.configOptions.userInfo?.enableRequest) {
            try {
                if(this.account) {
                    const accessToken = await this.getProfileTokenPopup();
                    if(accessToken && accessToken.length > 0) {
                        // Use the access_token to call the MS Graph User Profile endpoint (Not the same as OIDC's /userinfo endpoint)
                        const response = await this.callEndpointWithToken<UserInfo>(GRAPH_ME_ENDPOINT, accessToken);
                        this.userInfo = response as UserInfo;
                        // Do something with the userInfo
                        //this.logger.info("User Info", this.userInfo);
                    }
                }
            }
            catch(error) {
                // TODO[MW]: Can we log the custom error object (MSGraphError) that occurs in callEndpointWithToken?
                this.logger.warn("Could not fetch additional User Info", error);
            }
        }

        if(this.account && this.idToken) {
            this.isSignedIn = !!(this.account && this.idToken);
            this.taskQueue.queueTask(() => {
                this.events.publish(new SignOnEvent(this.isSignedIn, this.account?.name));
            });
        }
    }

    /**
     * Initiate the Log Out process using a Popup to show the Sign On Provider's logout page.
     */
    public async logOut(): Promise<void> {
        if(!this.msalInstance) {
            throw new Error("MSAL is not Initalized");
        }

        if(!this.account || !this.idToken) {
            this.selectAccount();
        }

        // set logout redirect.  IdP specific setting has priority over sign on provider setting
        const idp = this.account?.idTokenClaims?.idp;
        const idpLogoutRedirectUri = idp && this.configOptions.idp ? this.configOptions.idp[idp]?.logoutRedirectUri : undefined;
        const mainWindowRedirectUri = idpLogoutRedirectUri || this.configOptions.logout?.redirectUri;

        /**
         * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
         */

        const logoutRequest: msal.EndSessionPopupRequest = {
            account: this.account,
            idTokenHint: this.idToken ?? undefined,  // May be required by AD instance, and triggers promptless logout
            // postLogoutRedirectUri: this.env.resolveUrl('~/signon/blank')  // URI to navigate to after logout at the Sign On Provider and will be opened in the popup window.
            mainWindowRedirectUri: mainWindowRedirectUri  // URI the app will be redirected after logout
        };

        /**
         * Log Out process involving a poup
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md
         */
        await this.msalInstance.logoutPopup(logoutRequest);
        this.isSignedIn = false;
    }

    public getIdToken(): string | null {
        if(this.isSignedIn) {
            return this.idToken;
        }
        return null;
    }

    /**
     * Handle Redirect & Popup Login Request Response
     */
    protected handleRequestResponse = (response: msal.AuthenticationResult | null) => {
        /**
         * To see the full list of response object properties, visit:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
         */

        if(response !== null) {
            // Handling a successful authentication flow
            this.account = response.account;
            this.idToken = response.idToken;
            if(this.msalInstance && this.account) { this.msalInstance.setActiveAccount(this.account); }
        } else {
            this.selectAccount();
        }
    }

    /**
     * Calls getAllAccounts and determines the correct account to sign into.
     * If multiples found, defaults to first account found in cache.
     */
    protected selectAccount() {
        if(!this.msalInstance) {
            this.logger.error("MSAL is not Initalized");
            return;
        }

        /**
         * See here for more info on account retrieval:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
         */

        const currentAccounts = this.msalInstance.getAllAccounts();
        if(!currentAccounts || currentAccounts.length < 1) {
            this.logger.warn("No accounts detected");
            return;
        } else if(currentAccounts.length > 1) {
            this.logger.warn("Multiple accounts detected, defaulting to the first one.");
            this.account = currentAccounts[0];
        } else if(currentAccounts.length === 1) {
            this.account = currentAccounts[0];
        }

        // Get the account's ID token
        if(this.account?.homeAccountId) {
            const currentAccount = this.msalInstance.getAccountByHomeId(this.account.homeAccountId);
            this.idToken = currentAccount?.idToken ?? this.getIdTokenFromCache(this.account.homeAccountId) ?? null;
        }

        this.msalInstance.setActiveAccount(this.account);
    }

    /**
     * Get an account's ID token from the browser cache
     * @param homeAccountId
     * @returns The encoded ID token from cache
     */
    protected getIdTokenFromCache(homeAccountId: string): string | null {
        if(!this.msalInstance) {
            this.logger.error("MSAL is not Initalized");
            return null;
        }

        const cache = this.msalInstance.getTokenCache();
        // @ts-ignore
        // This isn't exposed by MSAL.js type definitions
        const idTokenEntity = cache.storage.getIdToken(homeAccountId);
        return idTokenEntity?.secret;
    }

    /**
     * Request's an access_token to fetch the User's Profile data from MS Graph API.
     * It will attempt to make this request silently, or falls back to interactive popup.
     * @returns An access token, if successful
     */
    protected async getProfileTokenPopup(): Promise<string | null> {
        if(!this.msalInstance) {
            this.logger.error("MSAL is not Initalized");
            return null;
        }

        // https://azuread.github.io/microsoft-authentication-library-for-js/ref/modules/_azure_msal_browser.html#silentrequest
        const silentProfileRequest: msal.SilentRequest = {
            scopes: this.userInfoRequestScopes,
            account: this.account ?? undefined,
            forceRefresh: false   // Set this to "true" to skip a cached token and go to the server to get a new token
        }

        const interactiveProfileRequest: msal.PopupRequest = {
            scopes: this.userInfoRequestScopes,
            redirectUri: this.env.resolveUrl('~/signon/blank')
        }

        if(this.account) {
            // Provide a login hint if an interactive request is necessary
            if(this.account.idTokenClaims?.preferred_username || this.account.username) {
                interactiveProfileRequest.loginHint = this.account.idTokenClaims?.preferred_username ?? this.account.username;
            }
        }

        // Make a silent request for the access token, otherwise use an interactive popup
        try {
            const { accessToken } = await this.msalInstance.acquireTokenSilent(silentProfileRequest);
            return accessToken;
        }
        catch(error) {
            this.logger.warn("Silent token acquisition failed");
            if(error instanceof msal.InteractionRequiredAuthError) {
                this.logger.debug("Acquiring token using interactive popup");
                const { accessToken } = await this.msalInstance.acquireTokenPopup(interactiveProfileRequest);
                return accessToken;
            } else {
                throw error;
            }
        }
    }

    protected handleMsalLogoutSuccessEvent(message: msal.EventMessage) {
        if(message.interactionType != 'popup') {
            this.logger.warn("Unsupported Interaction Type")
            return;
        }

        // Clear local account properties
        this.account = null;
        this.idToken = null;

        // Save the EndSession payload for reference
        const endSessionRequest = message.payload as msal.EndSessionPopupRequest;

        this.endSessionObject = {
            isSuccessful: true,
            successPayload: endSessionRequest  // This may not be useful, but for now, it's there in-case we need it
        };
    }


    protected handleMsalLogoutFailureEvent(message: msal.EventMessage) {
        const endSessionError = message.payload as msal.AuthError;

        // Save the EndSession payload for reference
        this.endSessionObject = {
            isSuccessful: false,
            failurePayload: endSessionError  // This may not be useful, but for now, it's there in-case we need it
        };
    }

    protected handleMsalLogoutEndEvent(message: msal.EventMessage) {
        this.taskQueue.queueTask(() => {
            if(!this.endSessionObject) { return; }
            this.events.publish(new SignOutEvent(this.endSessionObject.isSuccessful, !!this.endSessionObject.successPayload?.mainWindowRedirectUri));
            this.endSessionObject = null;
        });
    }


    /**
     * Makes an Authorization "Bearer" request with the given accessToken to the given endpoint.
     * @param endpoint
     * @param accessToken
     */
    private async callEndpointWithToken<T>(endpoint: string, accessToken: string): Promise<T> {
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        this.logger.debug("request made at: " + new Date().toString());

        const response = await fetch(endpoint, options);
        if(response.ok) {
            return (await response.json()) as T;
        }
        else {
            if(response.headers.get("Content-Type")?.includes('json')) {
                const error = await response.json();
                throw error;
            }
            const error = await response.text();
            throw error;
        }
    }

    public async getAccessToken(): Promise<string | undefined> {
        const activeAccount = await this.msalInstance?.getActiveAccount();

        //if account is no longer active or service instance is not initialized (can occur from reload), initialize it
        if(!activeAccount) {
            let signOnProviderCode = this.env.user.signOnProvider?.code || '';
            try {
                await this.init(signOnProviderCode); //until case correction has been fixed, all keys and codes should be lowercase.
            } catch(error) {
                this.logger.error(error);
                throw new Error("Error while retrieving new AccessToken");
            }
        }
        const request = {
            scopes: this.loginRequestScopes,
            account: activeAccount || undefined,
        };

        try {
            const response = await this.msalInstance?.acquireTokenSilent(request);
            const accessToken = response?.accessToken;

            return accessToken;
        } catch(error) {
            if(error instanceof msal.InteractionRequiredAuthError) {
                await this.msalInstance?.acquireTokenRedirect(request);
            }
            this.logger.error(error);
            return undefined;
        }
    }

    public getClaimFromAccount(claimKey: string): string {
        if(this.account?.idTokenClaims && claimKey in this.account.idTokenClaims) {
            return this.account.idTokenClaims[claimKey] as string;
        }
        return '';
    }
}
